import { type ClassValue, clsx } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * La escala tipografica del repo es POR ROLES (`text-caption`, `text-micro`,
 * `text-nano`… — `tailwind.config.js` → `fontSize`), y `tailwind-merge` no la
 * conoce. Ante un `text-<algo>` que no reconoce como tamano, lo clasifica como
 * COLOR de texto — y entonces `text-micro` y `text-destructive` le parecen la
 * misma propiedad y **borra uno de los dos**.
 *
 * Medido antes de arreglarlo:
 *
 *   cn('bg-primary text-primary-foreground shadow', 'text-caption')
 *     → 'bg-primary shadow text-caption'      // el color, fuera
 *   cn('text-destructive', 'text-micro')  → 'text-micro'        // color fuera
 *   cn('text-success', 'text-nano')       → 'text-nano'         // color fuera
 *   cn('text-destructive', 'text-sm')     → 'text-destructive text-sm'  // OK
 *
 * No es teorico: era la ultima violacion de contraste del ERP. El boton de la
 * tarjeta de Autopilot llevaba `text-primary-foreground` de su variante y un
 * `text-caption` en su `className`; se quedaba sin color, heredaba `foreground`
 * y pintaba texto oscuro sobre el azul de marca — 2.38 contra el 4.5 de AA.
 *
 * Y muerde en las dos direcciones segun el orden: en `text-nano … text-muted-
 * foreground` el que desaparece es el TAMANO. `text-micro` tiene 488 usos y
 * `text-nano` 693.
 *
 * Declarando la escala aqui, las dos clases sobreviven porque dejan de ser el
 * mismo grupo. Si se anade un rol nuevo a `tailwind.config.js`, va tambien aqui:
 * lo vigila `cnRolesTipograficos.test.ts`.
 */
export const ROLES_TIPOGRAFICOS = [
  'display', 'h1', 'h2', 'h3', 'title', 'body', 'body-sm', 'caption', 'micro', 'nano',
] as const;

const twMerge = extendTailwindMerge({
  extend: { classGroups: { 'font-size': [{ text: [...ROLES_TIPOGRAFICOS] }] } },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// ─── Date formatting utilities (ISO 25010 — consistency) ───

// timeZone: 'UTC' below — Prisma `@db.Date` arrives as `YYYY-MM-DDT00:00:00.000Z`;
// formatting in local TZ would shift the calendar day by -1 in negative TZs.

/** Format ISO date string → "23 feb 2026" (locale de presentación de la sesión) */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(_displayLocale, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/** Format ISO date string → "23/02/2026" (locale de presentación de la sesión) */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(_displayLocale, { timeZone: 'UTC' });
}

/** Format ISO date string → "23 feb" (no year — for recent dates; locale de presentación de la sesión) */
export function formatDateCompact(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(_displayLocale, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

/** Get today as YYYY-MM-DD string (for form defaults) */
export function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

/** Format ISO date to YYYY-MM-DD (for form values) */
export function toDateInput(dateStr: string | null | undefined): string {
  if (!dateStr) return '';
  return new Date(dateStr).toISOString().split('T')[0];
}

// ── Divisa por defecto ambiental (multidivisa, QA sweep) ─────────────────────
//
// `formatCurrency`/`formatCurrencyExact` tienen ~270 call sites que NO pasan
// divisa, así que una empresa con `functional_currency` distinta de EUR (p.ej.
// USD) mostraba TODOS los importes en €. En vez de migrar 270 call sites (con el
// riesgo de mezclar divisas en la misma pantalla), fijamos la divisa por defecto
// de la sesión una sola vez al cargar/cambiar de empresa (ver setDefaultCurrency
// en useApp). El default del parámetro se evalúa en CADA llamada, así que todos
// los call sites sin divisa explícita heredan la divisa funcional de la empresa
// activa. Las pantallas contables que pasan divisa explícita (metadata de la
// respuesta) siguen ganando.
let _defaultCurrency = 'EUR';

/** Fija la divisa por defecto de la sesión (divisa funcional de la empresa activa). */
export function setDefaultCurrency(currency?: string | null): void {
  _defaultCurrency = currency || 'EUR';
}

/** Divisa por defecto actual de la sesión. */
export function getDefaultCurrency(): string {
  return _defaultCurrency;
}

// ── Locale de presentación (formato de fecha/número) ──────────────────────────
//
// `display_locale` (user_preferences) es la preferencia PERSONAL de cómo se
// muestran fecha/número (orden DD/MM vs MM/DD, separador decimal coma/punto,
// posición del símbolo de moneda) — BCP-47, distinto de `locale_override`
// (idioma de los TEXTOS de la UI) y de `functional_currency` (divisa CONTABLE).
// Mismo patrón que `_defaultCurrency` de arriba: variable de sesión que los
// formateadores consultan en cada llamada, para no tener que pasar el locale en
// cada uno de los cientos de call sites existentes. Se sincroniza en TopBar.jsx
// (mismo sitio que hidrata locale_override) al cargar/cambiar preferencias.
//
// NO aplica a documentos fiscales/legales (facturas/nóminas PDF, AEAT/SII/
// VeriFACTU): esos usan `lib/dateFormatter.ts` (Europe/Madrid, es-ES fijo por
// RD 1619/2012) y los formateadores del backend, que ignoran esta preferencia.
let _displayLocale = 'es-ES';

export interface DisplayLocaleOption { code: string; label: string; example: string }

/** Catálogo de presets ofrecidos al usuario (selector de "Formato regional"). */
export const DISPLAY_LOCALES: readonly DisplayLocaleOption[] = [
  { code: 'es-ES', label: 'España', example: '31/12/2026 · 1.234,56 €' },
  { code: 'en-GB', label: 'Reino Unido', example: '31/12/2026 · £1,234.56' },
  { code: 'en-US', label: 'Estados Unidos', example: '12/31/2026 · $1,234.56' },
  { code: 'de-DE', label: 'Alemania', example: '31.12.2026 · 1.234,56 €' },
];

/** Fija el locale de presentación de la sesión (preferencia del usuario activo). */
export function setDisplayLocale(locale?: string | null): void {
  _displayLocale = (locale && DISPLAY_LOCALES.some((d) => d.code === locale)) ? locale : 'es-ES';
}

/** Locale de presentación actual de la sesión. */
export function getDisplayLocale(): string {
  return _displayLocale;
}

/**
 * Símbolo de una divisa ISO 4217 (default = divisa funcional de la sesión).
 * Derivado vía Intl para ser consistente con `formatCurrency` (locale es-ES:
 * EUR→"€", USD→"US$", GBP→"£", CHF→"CHF"). Para formatos compactos propios
 * ("€1,2k") que no pueden delegar en `formatCurrency`.
 */
export function getCurrencySymbol(currency: string = _defaultCurrency): string {
  try {
    const parts = new Intl.NumberFormat(_displayLocale, { style: 'currency', currency, minimumFractionDigits: 0 }).formatToParts(0);
    return parts.find((p) => p.type === 'currency')?.value || currency;
  } catch {
    return currency;
  }
}

/**
 * Format number as currency (no decimals). Divisa por defecto = divisa funcional
 * de la empresa activa (ver setDefaultCurrency); el 2º arg la sobreescribe
 * (pantallas contables multi-divisa que pasan `metadata.functional_currency`).
 * Formato (separadores/orden) = locale de presentación de la sesión (ver setDisplayLocale).
 */
export function formatCurrency(value: number | string | null | undefined, currency: string = _defaultCurrency): string {
  return new Intl.NumberFormat(_displayLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useGrouping: 'always',
  }).format(Number(value) || 0);
}

/** Format number as currency with decimals. Misma divisa por defecto y locale que formatCurrency. */
export function formatCurrencyExact(value: number | string | null | undefined, currency: string = _defaultCurrency): string {
  return new Intl.NumberFormat(_displayLocale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always',
  }).format(Number(value) || 0);
}

/** Format number as percentage (e.g. 18.5 → "18,5 %"), locale de presentación de la sesión */
export function formatPercent(value: number | string | null | undefined, decimals = 1): string {
  return new Intl.NumberFormat(_displayLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(Number(value) || 0) + ' %';
}

/** Format number with locale (no currency symbol), locale de presentación de la sesión */
export function formatNumber(value: number | string | null | undefined, decimals = 0): string {
  return new Intl.NumberFormat(_displayLocale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: 'always',
  }).format(Number(value) || 0);
}

/** Safe JSON parse — returns fallback on error */
export function safeJsonParse<T = any>(str: string | null | undefined, fallback: T | null = null): T | null {
  try { return JSON.parse(str!); } catch { return fallback; }
}

/**
 * Parse a value to number, accepting es-ES ("1.234,56"), en-US ("1,234.56"),
 * Prisma Decimal strings ("1234.56") or already-numeric values.
 * Returns null when the value is empty/invalid (callers decide the fallback).
 */
export function currencyToNumber(value: unknown): number | null {
  if (value == null) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // El símbolo de moneda y los espacios son RUIDO, no información: no dicen nada
  // sobre la convención decimal. Se quitan antes de mirar los separadores, porque
  // «100 €» tiene exactamente una interpretación y rechazarlo sería fallar en algo
  // que cualquiera teclea o pega —el propio `formatCurrencyES` emite ese formato,
  // con un espacio duro delante del €—. Lo que sí es ambiguo (`1.234`) se decide
  // más abajo, y no se resuelve aquí.
  const s = String(value).replace(/[\s\u00a0€]/g, '');
  if (!s) return null;
  const lastComma = s.lastIndexOf(',');
  const lastDot = s.lastIndexOf('.');
  let normalized: string;
  if (lastComma > -1 && lastDot > -1) {
    normalized = lastComma > lastDot
      ? s.replace(/\./g, '').replace(',', '.')   // "1.234,56" → "1234.56"
      : s.replace(/,/g, '');                     // "1,234.56" → "1234.56"
  } else if (lastComma > -1) {
    normalized = s.replace(',', '.');            // "1234,56" → "1234.56"
  } else {
    normalized = s;                              // "1234.56" or "1234"
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

// ─── Shared constants ───

export const PROVINCIAS: string[] = [
  'Álava', 'Albacete', 'Alicante', 'Almería', 'Asturias', 'Ávila', 'Badajoz',
  'Barcelona', 'Burgos', 'Cáceres', 'Cádiz', 'Cantabria', 'Castellón', 'Ceuta',
  'Ciudad Real', 'Córdoba', 'Cuenca', 'Gerona', 'Granada', 'Guadalajara',
  'Guipúzcoa', 'Huelva', 'Huesca', 'Islas Baleares', 'Jaén', 'La Coruña',
  'La Rioja', 'Las Palmas', 'León', 'Lérida', 'Lugo', 'Madrid', 'Málaga',
  'Melilla', 'Murcia', 'Navarra', 'Orense', 'Palencia', 'Pontevedra',
  'Salamanca', 'Santa Cruz de Tenerife', 'Segovia', 'Sevilla', 'Soria',
  'Tarragona', 'Teruel', 'Toledo', 'Valencia', 'Valladolid', 'Vizcaya',
  'Zamora', 'Zaragoza',
];

/**
 * Catálogo de países de la ficha de contacto. El VALOR es siempre ISO-2 porque el
 * backend deriva de él la fiscalidad de la operación: `natureFromCountry`
 * (`expenseOperationNature.ts`) decide interior / intracom / import reusando
 * `isEuExceptSpain`, el mismo predicado que filtra el Modelo 349.
 *
 * Por eso NO hay opción "Otro": un valor que `isEuExceptSpain` no reconoce cae a
 * `import` y la compra se declara con casillas de DUA en vez de autorrepercutirse
 * (303 casillas 10/11→36/37) — sin ningún error visible. Un país que falte aquí es
 * una declaración mal hecha, así que la UE va COMPLETA (los 26 restantes).
 *
 * Las etiquetas usan la grafía de `EU_COUNTRIES_ES` (`modelo349.ts`) para que el dato
 * siga siendo reconocible aunque algún camino guarde el nombre en vez del código.
 */
export interface Pais { code: string; label: string; eu: boolean }

export const PAISES: readonly Pais[] = [
  // España primero: es el caso por defecto de la inmensa mayoría de fichas.
  { code: 'ES', label: 'España', eu: true },
  // ─── UE-27 (resto) — intracomunitario ───
  { code: 'AT', label: 'Austria', eu: true },
  { code: 'BE', label: 'Bélgica', eu: true },
  { code: 'BG', label: 'Bulgaria', eu: true },
  { code: 'CY', label: 'Chipre', eu: true },
  { code: 'CZ', label: 'República Checa', eu: true },
  { code: 'DE', label: 'Alemania', eu: true },
  { code: 'DK', label: 'Dinamarca', eu: true },
  { code: 'EE', label: 'Estonia', eu: true },
  { code: 'FI', label: 'Finlandia', eu: true },
  { code: 'FR', label: 'Francia', eu: true },
  { code: 'GR', label: 'Grecia', eu: true },
  { code: 'HR', label: 'Croacia', eu: true },
  { code: 'HU', label: 'Hungría', eu: true },
  { code: 'IE', label: 'Irlanda', eu: true },
  { code: 'IT', label: 'Italia', eu: true },
  { code: 'LT', label: 'Lituania', eu: true },
  { code: 'LU', label: 'Luxemburgo', eu: true },
  { code: 'LV', label: 'Letonia', eu: true },
  { code: 'MT', label: 'Malta', eu: true },
  { code: 'NL', label: 'Países Bajos', eu: true },
  { code: 'PL', label: 'Polonia', eu: true },
  { code: 'PT', label: 'Portugal', eu: true },
  { code: 'RO', label: 'Rumanía', eu: true },
  { code: 'SE', label: 'Suecia', eu: true },
  { code: 'SI', label: 'Eslovenia', eu: true },
  { code: 'SK', label: 'Eslovaquia', eu: true },
  // ─── Terceros países frecuentes — la operación entra por aduana (DUA) ───
  { code: 'AD', label: 'Andorra', eu: false },
  { code: 'AR', label: 'Argentina', eu: false },
  { code: 'BR', label: 'Brasil', eu: false },
  { code: 'CH', label: 'Suiza', eu: false },
  { code: 'CN', label: 'China', eu: false },
  { code: 'GB', label: 'Reino Unido', eu: false },
  { code: 'JP', label: 'Japón', eu: false },
  { code: 'MA', label: 'Marruecos', eu: false },
  { code: 'MX', label: 'México', eu: false },
  { code: 'NO', label: 'Noruega', eu: false },
  { code: 'TR', label: 'Turquía', eu: false },
  { code: 'US', label: 'Estados Unidos', eu: false },
];
